import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Plane, Phone, Globe, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const navLinks = [
  { key: 'home', path: '/' },
  { key: 'about', path: '/ueber-uns' },
  { key: 'services', path: '/leistungen' },
  { key: 'pricing', path: '/preise' },
  { key: 'faq', path: '/faq' },
  { key: 'gallery', path: '/galerie' },
  { key: 'contact', path: '/kontakt' },
];

const languages = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const location = useLocation();
  const { language, changeLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setLangMenuOpen(false);
  }, [location]);

  const currentLang = languages.find(l => l.code === language);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'glass py-3' : 'bg-transparent py-5'
        }`}
        data-testid="navbar"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                <Plane className="w-6 h-6 text-black" />
              </div>
              <span className="text-xl md:text-2xl font-bold tracking-tight">
                Parking<span className="text-[#D4AF37]">DUS</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors duration-200 hover:text-[#D4AF37] ${
                    location.pathname === link.path ? 'text-[#D4AF37]' : 'text-white/80'
                  }`}
                  data-testid={`nav-link-${link.key}`}
                >
                  {t.nav[link.key]}
                </Link>
              ))}
            </div>

            {/* CTA, Language Switcher & Contact */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Switcher */}
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white/70 hover:text-white"
                  data-testid="language-switcher"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm">{currentLang?.flag}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {langMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full right-0 mt-2 bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden shadow-xl"
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            changeLanguage(lang.code);
                            setLangMenuOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-white/5 transition-colors ${
                            language === lang.code ? 'text-[#D4AF37] bg-white/5' : 'text-white/80'
                          }`}
                          data-testid={`lang-${lang.code}`}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <a
                href="tel:+4915126777707"
                className="flex items-center gap-2 text-sm text-white/70 hover:text-[#D4AF37] transition-colors"
                data-testid="phone-link"
              >
                <Phone className="w-4 h-4" />
                <span>+49 151 26777707</span>
              </a>
              <a
                href="https://mcdgroup.de/buchungssystem/index.php"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#D4AF37] text-black px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-[#E5C34A] transition-all duration-300 transform hover:-translate-y-0.5 gold-glow"
                data-testid="book-now-btn"
              >
                {t.nav.bookNow}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-white hover:text-[#D4AF37] transition-colors"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-black/95 backdrop-blur-xl" />
            <div className="relative pt-24 px-6 pb-8 h-full overflow-y-auto">
              <motion.div
                initial="closed"
                animate="open"
                variants={{
                  open: { transition: { staggerChildren: 0.1 } },
                  closed: {},
                }}
                className="flex flex-col gap-2"
              >
                {navLinks.map((link) => (
                  <motion.div
                    key={link.path}
                    variants={{
                      open: { opacity: 1, x: 0 },
                      closed: { opacity: 0, x: -20 },
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <Link
                      to={link.path}
                      className={`block py-4 px-4 text-2xl font-semibold rounded-xl transition-all duration-300 ${
                        location.pathname === link.path
                          ? 'text-[#D4AF37] bg-white/5'
                          : 'text-white hover:text-[#D4AF37] hover:bg-white/5'
                      }`}
                      data-testid={`mobile-nav-link-${link.key}`}
                    >
                      {t.nav[link.key]}
                    </Link>
                  </motion.div>
                ))}
                
                {/* Language Switcher Mobile */}
                <motion.div
                  variants={{
                    open: { opacity: 1, x: 0 },
                    closed: { opacity: 0, x: -20 },
                  }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 mb-2"
                >
                  <div className="flex gap-2 px-4">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`flex-1 py-3 px-4 rounded-xl text-center transition-all ${
                          language === lang.code
                            ? 'bg-[#D4AF37] text-black font-semibold'
                            : 'bg-white/5 text-white/70 hover:bg-white/10'
                        }`}
                        data-testid={`mobile-lang-${lang.code}`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
                
                <motion.div
                  variants={{
                    open: { opacity: 1, y: 0 },
                    closed: { opacity: 0, y: 20 },
                  }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                  className="mt-4 space-y-4"
                >
                  <a
                    href="tel:+4915126777707"
                    className="flex items-center justify-center gap-3 py-4 px-6 rounded-xl border border-white/10 text-white hover:border-[#D4AF37] transition-all"
                    data-testid="mobile-phone-link"
                  >
                    <Phone className="w-5 h-5" />
                    <span className="text-lg">+49 151 26777707</span>
                  </a>
                  <a
                    href="https://mcdgroup.de/buchungssystem/index.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center py-4 px-6 rounded-xl bg-[#D4AF37] text-black font-bold text-lg hover:bg-[#E5C34A] transition-all gold-glow"
                    data-testid="mobile-book-now-btn"
                  >
                    {t.nav.bookNow}
                  </a>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
